/**
 * The queue's state, off the scoring screen. See `queueNotice` for what it says and when.
 *
 * Never over the pad: `main.tsx` withholds it there, where the pill already says the same
 * thing and nothing may crowd the loop.
 */
import { useEffect, useState } from "react";
import type { DrainSummary, Drainer } from "@sportsos/sync";
import { queueNotice } from "./queueNotice.ts";

export function QueueBanner({ drainer }: { readonly drainer: Drainer }): React.ReactElement | null {
  const [summary, setSummary] = useState<DrainSummary>(() => drainer.summary());
  useEffect(() => {
    setSummary(drainer.summary());
    return drainer.subscribe(setSummary);
  }, [drainer]);

  const notice = queueNotice(summary);
  if (notice === null) return null;

  // Offered here as well as on the pad: a scorer who never reopens that match would otherwise
  // have no way to say "these are not mine to send". Confirmed first — this is the one place
  // that deletes anything from the ledger on this device.
  const discard = (): void => {
    if (
      window.confirm(
        "Discard the balls another account scored? They will not be sent, and this cannot be undone.",
      )
    ) {
      void drainer.discardForeign().catch(() => undefined);
    }
  };
  return (
    <div
      className="guest-banner queue-banner"
      role="alert"
      style={notice.tone === "bad" ? { background: "var(--ds-danger-100)", color: "var(--ds-danger-600)" } : undefined}
    >
      <span>{notice.text}</span>
      {summary.foreign > 0 && (
        <button className="ds-btn ds-btn--sm" type="button" onClick={discard}>
          Discard {summary.foreign === 1 ? "it" : "them"}
        </button>
      )}
    </div>
  );
}
